import * as _ from 'underscore';

import { IErrorItem, MultipleItemResult } from './MultipleItemResult';

/** User-displayable values about a particular item/error pair */
export interface IErrorDetail {
  itemName: string;
  itemUrl?: string;
  errorMessage: string;
}

/**
 * Functions that specify how to map an actual API error result to user string values
 */
export interface MultipleItemResultTranslator<T> {
  describeSuccessSummary(successCount: number): string;
  describeErrorSummary(errorCount: number): string;
  describeErrorDetail(errorItem: IErrorItem<T>): IErrorDetail;
  successActionMessage?(successCount: number): string;
}

/**
 * The actual user strings to display after a specific instance of a "content action" on multiple items
 */
export interface MultipleItemResultNotificationValues {
  successSummary?: string;
  successActionMessage?: string;
  successAction?: () => void;
  errorSummary?: string;
  errorDetails?: IErrorDetail[];
}

/**
 * From a MultipleItemResult, creates the user strings to display after a specific instance of a
 * "content action" on multiple items.
 * @param result A MultipleItemResult
 * @param translator Functions that specify how to translate the errors into strings
 */
export function createMultipleItemResultNotificationValues<T>(
  result: MultipleItemResult<T>,
  translator: MultipleItemResultTranslator<T>,
  successAction?: () => void
): MultipleItemResultNotificationValues {

  const options: MultipleItemResultNotificationValues = {};

  if (result.anySucceeded()) {
    options.successSummary = translator.describeSuccessSummary(result.successCount);
    if (translator.successActionMessage) {
      options.successAction = successAction;
      options.successActionMessage = translator.successActionMessage(result.successCount);
    }
  }
  if (result.anyFailed()) {
    options.errorSummary = translator.describeErrorSummary(result.errorCount);
    options.errorDetails = _.compact(_.map(
      result.errorItems(),
      (errorItem: IErrorItem<T>) => translator.describeErrorDetail(errorItem)
    ));
  }

  return options;
}
